import { forwardRef, type JSX, type ReactNode, type RefObject } from 'react';

import { TreeMapInternal } from './TreeMapInternal.js';
import { TreeMapLegendRenderer } from './TreeMapLegend/TreeMapLegendRenderer.js';
import { TreeMapOverlay } from './TreeMapOverlay/TreeMapOverlay.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../../core/types/data-props.js';
import { MaskingProps } from '../../../core/types/masking-props.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { ChartLayout } from '../../core/components/chart-layout/ChartLayout.js';
import type { ChartLayoutRef } from '../../core/components/chart-layout/types/chart-layout.js';
import { OverlayProvider } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { useChartLegend } from '../context/ChartLegendContext.js';
import { TreeMapAggregationDataProvider } from '../providers/TreeMapAggregationDataProvider.js';
import { TreeMapHighlightingMapProvider } from '../providers/TreeMapHighlightingMapProvider.js';
import { TreeMapInternalDataProvider } from '../providers/TreeMapInternalDataProvider.js';

interface TreemapChartLayoutProps
  extends StylingProps,
    MaskingProps,
    DataTestId,
    BehaviorTrackingProps {
  width: string | number;
  height: string | number;
  emptyState?: ReactNode;
  errorState: ReactNode | ((errorMessage: string) => JSX.Element) | undefined;
  isEmpty: boolean;
  loading: boolean;
  chartLayoutRef: RefObject<ChartLayoutRef | null>;
  'data-dt-component': string;
}

export const TreeMapChartLayout = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  TreemapChartLayoutProps
>((props, forwardedRef) => {
  const {
    chartLayoutRef,
    width,
    height,
    emptyState,
    errorState,
    isEmpty,
    loading,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    'data-dt-component': dataDtComponent,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const {
    hidden: legendHidden,
    position: legendPosition,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    onRatioChange,
  } = useChartLegend();

  const legendRatio = initialRatio ?? ratio;

  return (
    <ChartLayout
      {...behavioralTrackingProps}
      ref={chartLayoutRef}
      chartWidth={width}
      chartHeight={height}
      emptyState={emptyState}
      errorState={errorState}
      showLoader={loading}
      isEmpty={isEmpty}
      className={className}
      style={style}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-testid={dataTestId}
      data-dt-component={dataDtComponent}
    >
      <ChartLayout.Graph>
        <TreeMapInternalDataProvider>
          <TreeMapAggregationDataProvider>
            <TreeMapHighlightingMapProvider>
              <OverlayProvider>
                <TooltipProvider>
                  <div style={{ position: 'relative' }}>
                    <TreeMapInternal ref={forwardedRef} />
                    {loading ? null : <TreeMapOverlay />}
                  </div>
                </TooltipProvider>
              </OverlayProvider>
            </TreeMapHighlightingMapProvider>
          </TreeMapAggregationDataProvider>
        </TreeMapInternalDataProvider>
      </ChartLayout.Graph>
      {!legendHidden && (
        <ChartLayout.Legend
          position={legendPosition}
          initialRatio={legendRatio}
          onResize={onRatioChange}
        >
          <TreeMapLegendRenderer />
        </ChartLayout.Legend>
      )}
    </ChartLayout>
  );
});
